import React, { useState, useEffect } from 'react';
import { Typography } from '../../interfaces/typography';
import { getServicesManager } from '../../services';
import { History } from '../History/History';
import { SessionDetailView } from '../History/views';
import { Alert, Button, CircularProgress } from '@mui/material';
import { PromptExample } from './promptExamples';

type ConfirmAction = 'save' | 'delete' | null;

interface AIPromptTemplateProps {
  title: string;
  description: string;
  placeholder: string;
  testResultLabel: string;
  examples: PromptExample[];
  getPrompt: () => Promise<{ team_id: string; set_by_user_id: string; prompt: string; updated_at: string } | null>;
  savePrompt: (prompt: string | null) => Promise<boolean>;
  testPrompt: (sessionId: string, prompt: string) => Promise<any>;
  extractTestResult: (result: any) => string | null;
  confirmSaveMessage: string;
  confirmDeleteMessage: string;
}

export function AIPromptTemplate({
  title,
  description,
  placeholder,
  testResultLabel,
  examples,
  getPrompt,
  savePrompt,
  testPrompt,
  extractTestResult,
  confirmSaveMessage,
  confirmDeleteMessage,
}: AIPromptTemplateProps) {
  const [prompt, setPrompt] = useState<string>('');
  const [savedPrompt, setSavedPrompt] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [selectedSessions, setSelectedSessions] = useState<string[]>([]);
  const [testing, setTesting] = useState<boolean>(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [showExamples, setShowExamples] = useState<boolean>(false);

  useEffect(() => {
    loadPrompt();
  }, []);

  const loadPrompt = async () => {
    try {
      setLoading(true);
      const result = await getPrompt();
      if (result && result.prompt) {
        setPrompt(result.prompt);
        setSavedPrompt(result.prompt);
      }
    } catch (err) {
      console.error('Failed to load prompt:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClick = () => {
    setConfirmAction('save');
  };

  const handleDeleteClick = () => {
    setConfirmAction('delete');
  };

  const handleConfirm = async () => {
    if (confirmAction === 'save') {
      await performSave();
    } else if (confirmAction === 'delete') {
      await performDelete();
    }
    setConfirmAction(null);
  };

  const handleCancel = () => {
    setConfirmAction(null);
  };

  const performSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      const success = await savePrompt(prompt || null);
      if (success) {
        setSavedPrompt(prompt);
        setSuccessMessage('Prompt saved successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError('Failed to save prompt');
      }
    } catch (err) {
      console.error('Failed to save prompt:', err);
      setError('Failed to save prompt');
    } finally {
      setSaving(false);
    }
  };

  const performDelete = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      const success = await savePrompt(null);
      if (success) {
        setPrompt('');
        setSavedPrompt('');
        setSuccessMessage('Prompt deleted successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError('Failed to delete prompt');
      }
    } catch (err) {
      console.error('Failed to delete prompt:', err);
      setError('Failed to delete prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (selectedSessions.length === 0 || !prompt) return;

    try {
      setTesting(true);
      setError(null);
      setTestResult(null);
      const result = await testPrompt(selectedSessions[0], prompt);
      const extracted = extractTestResult(result);
      if (extracted) {
        setTestResult(extracted);
      } else {
        setError(`No ${testResultLabel.toLowerCase()} returned`);
      }
    } catch (err) {
      console.error('Failed to test prompt:', err);
      setError(`Failed to generate test ${testResultLabel.toLowerCase()}`);
    } finally {
      setTesting(false);
    }
  };

  const hasUnsavedChanges = prompt !== savedPrompt;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <Typography variant="h3" color="#1F2937" fontWeight={600} className="mb-3">
                {confirmAction === 'save' ? 'Save Prompt?' : 'Delete Prompt?'}
              </Typography>
              <Typography variant="mediumParagraph" color="#6B7280">
                {confirmAction === 'save' ? confirmSaveMessage : confirmDeleteMessage}
              </Typography>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <Button
                variant="outlined"
                fullWidth
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color={confirmAction === 'delete' ? 'error' : 'primary'}
                fullWidth
                onClick={handleConfirm}
              >
                {confirmAction === 'save' ? 'Save' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="h-full p-6">
        {/* Alerts */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" onClose={() => setSuccessMessage(null)} className="mb-4">
            {successMessage}
          </Alert>
        )}

        {/* Test Results - Only show when available */}
        {testResult && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <Typography variant="h4" color="#374151" fontWeight={500}>
                Test Results
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={() => setTestResult(null)}
              >
                Close
              </Button>
            </div>
            <div className="p-6">
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg max-h-96 overflow-auto">
                <Typography variant="smallParagraph" color="#1F2937" className="whitespace-pre-wrap">
                  {testResult}
                </Typography>
              </div>
            </div>
          </div>
        )}

        {/* Prompt Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <Typography variant="h3" color="#1F2937" fontWeight={600} className="mb-2">
                  {title}
                </Typography>
                <Typography variant="mediumParagraph" color="#6B7280">
                  {description}
                </Typography>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={() => setShowExamples(!showExamples)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                {showExamples ? 'Hide Examples' : 'View Example Prompts'}
              </button>

              <div className="flex gap-3">
                <button
                  onClick={handleTest}
                  disabled={testing || !prompt || selectedSessions.length === 0}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                    testing || !prompt || selectedSessions.length === 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  {testing ? 'Testing...' : 'Test'}
                </button>
                <button
                  onClick={handleSaveClick}
                  disabled={saving || !hasUnsavedChanges}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                    saving || !hasUnsavedChanges
                      ? 'bg-blue-300 text-white cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                  }`}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                {savedPrompt && (
                  <button
                    onClick={handleDeleteClick}
                    disabled={saving}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                      saving
                        ? 'bg-red-100 text-red-400 cursor-not-allowed'
                        : 'bg-white text-red-600 border border-red-300 hover:bg-red-50 hover:border-red-400'
                    }`}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>

            {/* Examples Section */}
            {showExamples && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <Typography variant="h4" color="#374151" fontWeight={500} className="mb-2">
                  Example Prompts
                </Typography>
                <Typography variant="smallParagraph" color="#6B7280" className="mb-4">
                  Click any example to use it as a starting point
                </Typography>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {examples.map((example, index) => (
                    <button
                      key={index}
                      onClick={() => setPrompt(example.prompt)}
                      className="text-left p-4 bg-gray-50 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 hover:shadow-md transition-all"
                    >
                      <Typography variant="smallParagraph" color="#1F2937" fontWeight={600} className="mb-1">
                        {example.name}
                      </Typography>
                      <Typography variant="smallParagraph" color="#6B7280" fontSize="13px">
                        {example.description}
                      </Typography>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="p-6">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={placeholder}
              className="w-full h-64 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
            />

            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <Typography variant="smallParagraph" color="#1E40AF" fontWeight={500} className="mb-1">
                Note
              </Typography>
              <Typography variant="smallParagraph" color="#1E40AF">
                The call transcript and metadata will be automatically formatted and appended to your prompt.
              </Typography>
            </div>
          </div>
        </div>

        {/* Session Selection */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <Typography variant="h4" color="#374151" fontWeight={500}>
              Select a Session to Test
            </Typography>
            <Typography variant="smallParagraph" color="#6B7280" className="mt-1">
              Choose a session to test your prompt against
            </Typography>
          </div>
          <div className="h-[600px] overflow-hidden">
            <History
              view={SessionDetailView.ANALYSIS}
              onSessionSelect={setSelectedSessions}
              selectedSessions={selectedSessions}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
